import type { ReactElement } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { Check, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge, Money } from '@/shared/components/ui'
import { getInvoiceLinks } from '@/features/invoices/api/invoicesService'
import InvoicePaymentsBadge from '@/features/invoices/components/InvoicePaymentsBadge'
import type { Invoice } from '@/features/invoices/schemas/invoicesSchema'

interface InvoiceCardProps {
  index: number
  invoice: Invoice
  isDualColumn: boolean
  canDrag: boolean
  showSatisfactionStatus: boolean
  recentlyLinked: boolean
  onEditInvoice: (invoice: Invoice) => void
}

export function InvoiceCard({
  index,
  invoice,
  isDualColumn,
  canDrag,
  showSatisfactionStatus,
  recentlyLinked,
  onEditInvoice,
}: InvoiceCardProps): ReactElement {
  const dateStr = format(new Date(invoice.saleDate), 'dd MMM')
  const primaryLabel = invoice.counterparty.trim() || invoice.invoiceNumber
  const secondaryLabel = invoice.invoiceNumber.trim() || '—'
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `invoice-${invoice.uuid}`,
    data: { type: 'invoice', invoiceUuid: invoice.uuid },
    disabled: !canDrag,
  })
  const { data: invoiceLinks } = useQuery({
    queryKey: ['invoices', 'links', invoice.uuid],
    queryFn: () => getInvoiceLinks(invoice.uuid),
    staleTime: 5 * 60 * 1000,
  })
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined
  const linkedPaymentsCount = invoiceLinks?.payments.length ?? 0
  const hasLinkedPayments = linkedPaymentsCount > 0
  const isPlatformDeducted = invoice.settlementType === 'PLATFORM_DEDUCTED'
  const isSettled = hasLinkedPayments || isPlatformDeducted

  return (
    <div
      style={style}
      ref={setNodeRef}
      {...(canDrag ? { ...listeners, ...attributes } : {})}
      className={cn(
        'rounded-xl border bg-card transition-all',
        'border-border',
        'hover:border-foreground/20',
        canDrag && 'cursor-grab active:cursor-grabbing',
        isDragging && 'opacity-40 shadow-lg z-50',
      )}
    >
      <div className="flex items-stretch">
        <div
          className={cn(
            'relative flex w-12 shrink-0 items-center justify-center border-r border-border/40 bg-muted/10 transition-colors',
            showSatisfactionStatus && isSettled && 'bg-emerald-500/12',
            recentlyLinked && 'bg-emerald-500/14 ring-1 ring-inset ring-emerald-400/25',
          )}
        >
          <span
            className={cn(
              'text-base font-semibold tabular-nums text-muted-foreground/35 transition-all duration-200',
              recentlyLinked && 'scale-90 opacity-0',
            )}
          >
            {index}
          </span>
          <Check
            className={cn(
              'pointer-events-none absolute size-4 text-[var(--border-positive)] transition-all duration-200',
              recentlyLinked ? 'scale-100 opacity-100' : 'scale-90 opacity-0',
            )}
            aria-hidden="true"
          />
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-between gap-4 p-4">
          <div className="min-w-0 flex-1">
            <p
              className={cn(
                'truncate text-[1.02rem] font-semibold leading-snug text-foreground',
                isDualColumn ? 'max-w-[28ch]' : 'max-w-[44ch]',
              )}
              title={primaryLabel}
            >
              {primaryLabel}
            </p>
            <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground/80">
              <span className="shrink-0 text-muted-foreground/55">{dateStr}</span>
              <span className="text-muted-foreground/45">|</span>
              <span
                className={cn(
                  'truncate text-muted-foreground/75',
                  isDualColumn ? 'max-w-[26ch]' : 'max-w-[40ch]',
                )}
                title={secondaryLabel}
              >
                {secondaryLabel}
              </span>
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1.5">
            <span
              className="cursor-text select-text text-sm font-semibold tabular-nums tracking-tight text-foreground/80"
              onPointerDown={event => event.stopPropagation()}
            >
              <Money value={invoice.grossAmount} currency={invoice.currency} minFraction={2} maxFraction={2} />
            </span>
            <div className="flex items-center gap-1.5">
              <InvoicePaymentsBadge
                invoiceUuid={invoice.uuid}
                linkedPaymentsCount={linkedPaymentsCount}
                settlementType={invoice.settlementType}
              />
              {invoice.hasFile ? (
                <Badge
                  asChild
                  variant="secondary"
                  className="h-7 gap-1 px-2 text-[11px] text-muted-foreground/80 transition-colors hover:bg-muted-foreground/20"
                >
                  <button
                    type="button"
                    onPointerDown={event => event.stopPropagation()}
                    onClick={event => {
                      event.stopPropagation()
                      onEditInvoice(invoice)
                    }}
                    aria-label={`Edit invoice ${invoice.invoiceNumber}`}
                  >
                    <FileText className="size-3.5" />
                    File
                  </button>
                </Badge>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
